/**
 * GraphSerializer — Bidirectional Graph ↔ JSON serialization.
 *
 * Handles versioning, migration, and validation on load.
 * Generic to support domain-specific node and variable types.
 */

import type {
  BaseGraphData,
  BaseGraphVariable,
  BaseNodeInstance,
  Edge,
  GraphMetadata,
  PortRef,
} from './types';

// ============================================================================
// Version
// ============================================================================

const FORMAT_VERSION = 1;

// ============================================================================
// Serialized format (JSON-friendly, compact)
// ============================================================================

/**
 * Serialized graph format — compact JSON representation.
 */
export interface SerializedGraph {
  readonly version: number;
  readonly id: string;
  readonly name: string;
  readonly nodes: SerializedNode[];
  readonly edges: SerializedEdge[];
  readonly variables: SerializedVariable[];
  readonly metadata?: GraphMetadata;
}

/**
 * Serialized node — position is split into x/y for compactness.
 */
export interface SerializedNode {
  readonly id: string;
  readonly type: string;
  readonly x: number;
  readonly y: number;
  readonly inputs?: Record<string, unknown>;
  readonly comment?: string;
  readonly config?: Record<string, unknown>;
  /** Additional domain-specific fields */
  readonly [key: string]: unknown;
}

/**
 * Serialized edge — short property names for compactness.
 */
export interface SerializedEdge {
  readonly id: string;
  readonly fn: string;   // from nodeId
  readonly fp: string;   // from portId
  readonly tn: string;   // to nodeId
  readonly tp: string;   // to portId
}

/**
 * Serialized variable.
 */
export interface SerializedVariable {
  readonly id: string;
  readonly name: string;
  readonly type: string;
  readonly value: unknown;
  readonly exposed?: boolean;
  readonly description?: string;
  /** Additional domain-specific fields */
  readonly [key: string]: unknown;
}

// ============================================================================
// Serialization options
// ============================================================================

/**
 * Options for serialization.
 */
export interface SerializeOptions<TNode, TVariable> {
  /**
   * Custom node serializer — extract additional fields from node.
   * Return an object with extra fields to include.
   */
  nodeExtras?: (node: TNode) => Record<string, unknown> | undefined;

  /**
   * Custom variable serializer — extract additional fields from variable.
   */
  variableExtras?: (variable: TVariable) => Record<string, unknown> | undefined;
}

/**
 * Options for deserialization.
 */
export interface DeserializeOptions<TNode, TVariable> {
  /**
   * Custom node deserializer — create node from serialized data.
   * If not provided, uses default deserialization.
   */
  nodeFromSerialized?: (raw: SerializedNode) => TNode;

  /**
   * Custom variable deserializer.
   */
  variableFromSerialized?: (raw: SerializedVariable) => TVariable;
}

// ============================================================================
// Serialize
// ============================================================================

/**
 * Serialize a graph to a compact JSON-serializable object.
 */
export function serializeGraph<
  TNode extends BaseNodeInstance,
  TVariable extends BaseGraphVariable,
>(
  graph: BaseGraphData<TNode, TVariable>,
  options?: SerializeOptions<TNode, TVariable>,
): SerializedGraph {
  return {
    version: FORMAT_VERSION,
    id: graph.id,
    name: graph.name,
    nodes: graph.nodes.map(n => serializeNode(n, options?.nodeExtras)),
    edges: graph.edges.map(e => serializeEdge(e)),
    variables: graph.variables.map(v => serializeVariable(v, options?.variableExtras)),
    metadata: graph.metadata,
  };
}

function serializeNode<TNode extends BaseNodeInstance>(
  node: TNode,
  getExtras?: (node: TNode) => Record<string, unknown> | undefined,
): SerializedNode {
  const out: Record<string, unknown> = {
    id: node.id,
    type: node.type,
    x: node.position.x,
    y: node.position.y,
  };

  if (Object.keys(node.inputValues).length > 0) {
    out.inputs = node.inputValues;
  }
  if (node.comment) out.comment = node.comment;
  if (node.config) out.config = node.config;

  // Add custom extras
  if (getExtras) {
    const extras = getExtras(node);
    if (extras) Object.assign(out, extras);
  }

  return out as SerializedNode;
}

function serializeEdge(edge: Edge): SerializedEdge {
  return {
    id: edge.id,
    fn: edge.from.nodeId,
    fp: edge.from.portId,
    tn: edge.to.nodeId,
    tp: edge.to.portId,
  };
}

function serializeVariable<TVariable extends BaseGraphVariable>(
  v: TVariable,
  getExtras?: (variable: TVariable) => Record<string, unknown> | undefined,
): SerializedVariable {
  const out: Record<string, unknown> = {
    id: v.id,
    name: v.name,
    type: v.type,
    value: v.defaultValue,
  };
  if (v.exposed) out.exposed = true;
  if (v.description) out.description = v.description;

  // Add custom extras
  if (getExtras) {
    const extras = getExtras(v);
    if (extras) Object.assign(out, extras);
  }

  return out as SerializedVariable;
}

// ============================================================================
// Deserialize
// ============================================================================

/**
 * Deserialize a JSON object back into graph data.
 * Throws on structural errors.
 */
export function deserializeGraph<
  TNode extends BaseNodeInstance = BaseNodeInstance,
  TVariable extends BaseGraphVariable = BaseGraphVariable,
>(
  json: unknown,
  options?: DeserializeOptions<TNode, TVariable>,
): BaseGraphData<TNode, TVariable> {
  if (!json || typeof json !== 'object') {
    throw new Error('Invalid graph data: expected an object');
  }

  const raw = json as Record<string, unknown>;
  const version = (raw.version as number) ?? 1;

  // Future: version migration
  if (version > FORMAT_VERSION) {
    throw new Error(`Graph version ${version} is newer than supported (${FORMAT_VERSION})`);
  }

  const id = expectString(raw, 'id', 'graph');
  const name = expectString(raw, 'name', 'Untitled');

  const rawNodes = Array.isArray(raw.nodes) ? raw.nodes : [];
  const rawEdges = Array.isArray(raw.edges) ? raw.edges : [];
  const rawVars = Array.isArray(raw.variables) ? raw.variables : [];

  const nodes: TNode[] = rawNodes.map(n =>
    options?.nodeFromSerialized
      ? options.nodeFromSerialized(n as SerializedNode)
      : deserializeNode<TNode>(n),
  );

  const edges: Edge[] = rawEdges.map(deserializeEdge);

  const variables: TVariable[] = rawVars.map(v =>
    options?.variableFromSerialized
      ? options.variableFromSerialized(v as SerializedVariable)
      : deserializeVariable<TVariable>(v),
  );

  return {
    id,
    name,
    nodes,
    edges,
    variables,
    metadata: raw.metadata as GraphMetadata | undefined,
  };
}

function deserializeNode<TNode extends BaseNodeInstance>(raw: unknown): TNode {
  const r = raw as Record<string, unknown>;
  const { id, type, x, y, inputs, comment, config, ...extras } = r;

  return {
    id: expectString(r, 'id'),
    type: expectString(r, 'type'),
    position: { x: (x as number) ?? 0, y: (y as number) ?? 0 },
    inputValues: (inputs as Record<string, unknown>) ?? {},
    comment: comment as string | undefined,
    config: config as Record<string, unknown> | undefined,
    ...extras,
  } as TNode;
}

function deserializeEdge(raw: unknown): Edge {
  const r = raw as Record<string, unknown>;
  return {
    id: expectString(r, 'id'),
    from: { nodeId: expectString(r, 'fn'), portId: expectString(r, 'fp') },
    to: { nodeId: expectString(r, 'tn'), portId: expectString(r, 'tp') },
  };
}

function deserializeVariable<TVariable extends BaseGraphVariable>(raw: unknown): TVariable {
  const r = raw as Record<string, unknown>;
  const { id, name, type, value, exposed, description, ...extras } = r;

  return {
    id: expectString(r, 'id'),
    name: expectString(r, 'name'),
    type: expectString(r, 'type'),
    defaultValue: value ?? null,
    exposed: !!exposed,
    description: description as string | undefined,
    ...extras,
  } as TVariable;
}

// ============================================================================
// Helpers
// ============================================================================

function expectString(obj: Record<string, unknown>, key: string, fallback?: string): string {
  const v = obj[key];
  if (typeof v === 'string') return v;
  if (fallback !== undefined) return fallback;
  throw new Error(`Missing required string field '${key}'`);
}

// ============================================================================
// JSON convenience
// ============================================================================

/**
 * Options for JSON serialization.
 */
export interface JsonSerializeOptions<TNode, TVariable> extends SerializeOptions<TNode, TVariable> {
  /** If true, produce formatted JSON with indentation */
  pretty?: boolean;
}

/**
 * Serialize a graph to a JSON string.
 */
export function graphToJSON<
  TNode extends BaseNodeInstance,
  TVariable extends BaseGraphVariable,
>(
  graph: BaseGraphData<TNode, TVariable>,
  options?: JsonSerializeOptions<TNode, TVariable>,
): string {
  return JSON.stringify(serializeGraph(graph, options), null, options?.pretty ? 2 : undefined);
}

/**
 * Parse a JSON string into graph data.
 */
export function graphFromJSON<
  TNode extends BaseNodeInstance = BaseNodeInstance,
  TVariable extends BaseGraphVariable = BaseGraphVariable,
>(
  json: string,
  options?: DeserializeOptions<TNode, TVariable>,
): BaseGraphData<TNode, TVariable> {
  return deserializeGraph(JSON.parse(json), options);
}
